import type { StatusTone } from "@/components/ui";
import type {
  CommunityAction,
  CommunityFeatureAction,
  CommunityFeatureStatus,
  CommunityHealthStatus,
  CommunityMembershipAction,
  CommunityMembershipStatus,
  CommunityOwnershipAction,
  CommunityOwnershipStatus,
  CommunityStatus,
  PostAction,
  PostStatus,
  VisibilityStatus,
} from "./types";

export function formatLabel(value: string): string {
  return value.replaceAll("_", " ");
}

export function communityStatusTone(status: CommunityStatus): StatusTone {
  switch (status) {
    case "active":
      return "success";
    case "draft":
      return "info";
    case "paused":
    case "restricted":
      return "warning";
    case "archived":
    case "closed":
      return "neutral";
    default:
      return "neutral";
  }
}

export function visibilityStatusTone(status: VisibilityStatus): StatusTone {
  switch (status) {
    case "visible":
      return "success";
    case "paused":
    case "hidden":
      return "warning";
    case "restricted":
      return "danger";
    default:
      return "neutral";
  }
}

export function healthStatusTone(
  status: CommunityHealthStatus | null,
): StatusTone {
  switch (status) {
    case "healthy":
      return "success";
    case "monitoring":
      return "info";
    case "at_risk":
    case "restricted":
      return "warning";
    case "critical":
      return "danger";
    default:
      return "neutral";
  }
}

export function membershipStatusTone(
  status: CommunityMembershipStatus,
): StatusTone {
  switch (status) {
    case "active":
      return "success";
    case "invited":
    case "requested":
      return "info";
    case "restricted":
      return "warning";
    case "left":
    case "removed":
      return "neutral";
    default:
      return "neutral";
  }
}

export function ownershipStatusTone(
  status: CommunityOwnershipStatus,
): StatusTone {
  switch (status) {
    case "active":
      return "success";
    case "proposed":
      return "info";
    case "suspended":
      return "warning";
    case "transferred":
    case "revoked":
      return "neutral";
    default:
      return "neutral";
  }
}

export function postStatusTone(status: PostStatus): StatusTone {
  switch (status) {
    case "published":
    case "edited":
      return "success";
    case "draft":
      return "info";
    case "restricted":
      return "warning";
    case "removed":
    case "archived":
      return "neutral";
    default:
      return "neutral";
  }
}

export function featureStatusTone(status: CommunityFeatureStatus): StatusTone {
  switch (status) {
    case "active":
    case "approved":
      return "success";
    case "proposed":
    case "scheduled":
      return "info";
    case "ended":
    case "removed":
      return "neutral";
    default:
      return "neutral";
  }
}

export const COMMUNITY_ACTIONS_FROM: Record<
  CommunityAction,
  readonly CommunityStatus[]
> = {
  activate: ["draft"],
  restrict: ["active"],
  pause: ["active"],
  restore: ["restricted", "paused"],
  archive: ["draft", "active", "restricted", "paused"],
  close: ["draft", "active", "restricted", "paused", "archived"],
};

export const MEMBERSHIP_ACTIONS_FROM: Record<
  CommunityMembershipAction,
  readonly CommunityMembershipStatus[]
> = {
  activate: ["invited", "requested", "restricted"],
  restrict: ["active"],
  leave: ["active", "restricted", "invited", "requested"],
  remove: ["invited", "requested", "active", "restricted"],
};

export const OWNERSHIP_ACTIONS_FROM: Record<
  CommunityOwnershipAction,
  readonly CommunityOwnershipStatus[]
> = {
  activate: ["proposed", "suspended"],
  transfer: ["active"],
  suspend: ["active"],
  revoke: ["proposed", "active", "suspended"],
};

export const POST_ACTIONS_FROM: Record<PostAction, readonly PostStatus[]> = {
  publish: ["draft", "edited"],
  edit: ["published", "edited"],
  restrict: ["published", "edited"],
  remove: ["draft", "published", "edited", "restricted"],
  archive: ["draft", "published", "edited", "restricted"],
};

export const FEATURE_ACTIONS_FROM: Record<
  CommunityFeatureAction,
  readonly CommunityFeatureStatus[]
> = {
  approve: ["proposed"],
  schedule: ["approved"],
  activate: ["approved", "scheduled"],
  end: ["active", "scheduled"],
  remove: ["proposed", "approved", "scheduled", "active", "ended"],
};

export function allowedCommunityActions(
  status: CommunityStatus,
): CommunityAction[] {
  return (Object.keys(COMMUNITY_ACTIONS_FROM) as CommunityAction[]).filter(
    (action) => COMMUNITY_ACTIONS_FROM[action].includes(status),
  );
}

export function allowedMembershipActions(
  status: CommunityMembershipStatus,
): CommunityMembershipAction[] {
  return (
    Object.keys(MEMBERSHIP_ACTIONS_FROM) as CommunityMembershipAction[]
  ).filter((action) => MEMBERSHIP_ACTIONS_FROM[action].includes(status));
}

export function allowedOwnershipActions(
  status: CommunityOwnershipStatus,
): CommunityOwnershipAction[] {
  return (
    Object.keys(OWNERSHIP_ACTIONS_FROM) as CommunityOwnershipAction[]
  ).filter((action) => OWNERSHIP_ACTIONS_FROM[action].includes(status));
}

export function allowedPostActions(status: PostStatus): PostAction[] {
  return (Object.keys(POST_ACTIONS_FROM) as PostAction[]).filter((action) =>
    POST_ACTIONS_FROM[action].includes(status),
  );
}

export function allowedFeatureActions(
  status: CommunityFeatureStatus,
): CommunityFeatureAction[] {
  return (Object.keys(FEATURE_ACTIONS_FROM) as CommunityFeatureAction[]).filter(
    (action) => FEATURE_ACTIONS_FROM[action].includes(status),
  );
}

/** Convert datetime-local (or any parseable date) to UTC ISO with offset. */
export function toIsoUtc(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return trimmed;
  return date.toISOString();
}
